/**
 * Static, curated guide for each SJDS surf spot. Merged at render-time with
 * live conditions from /api/surf/{beach_id}. Slugs match the backend BEACHES
 * table so `/surf/:slug` resolves to `/api/surf/:slug` seamlessly.
 *
 * Each beach carries an `i18n.es` block with Spanish translations for every
 * localized field. Use `localizeBeach(beach, lang)` below to pick the correct
 * copy at render time (English is the fallback).
 */
export const BEACH_GUIDES = {
  "playa-maderas": {
    id: "playa-maderas",
    slug: "maderas",
    name: "Playa Maderas",
    tagline: "The iconic SJDS surf beach — consistent A-frames, chill vibes.",
    difficulty: "Intermediate",
    difficultyLevel: 3,
    waveStyle: "A-frame beach break with lefts and rights",
    bestTide: "Low to mid rising",
    bestWind: "Offshore from the east (early morning)",
    boardRecommended: "Shortboard 6'0\"–6'6\" · fun-shape 6'8\"–7'2\"",
    bestSeasons: [
      { months: "Apr – Oct", label: "Prime swell (wet season)", quality: 5 },
      { months: "Nov – Mar", label: "Cleaner but smaller (dry season)", quality: 3 },
    ],
    hazards: ["Localized rip current on bigger swells", "Rocks at the far south end at low tide"],
    directions: {
      from: "San Juan del Sur town",
      distance: "8 km · 20 min by truck / shuttle",
      routeNote: "Unpaved road — 4×4 or shuttle recommended in rainy season",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Maderas",
      mapHref: "https://www.google.com/maps/place/Playa+Maderas,+Nicaragua",
    },
    localTips: [
      "Get there before 8am — light offshore winds die by 10am",
      "Bring cash for parking (US$3–5) and the beachfront bar",
      "Empty water bottles — the walk back up the hill is hot",
      "The peaks shift — walk 100m in either direction to find your wave",
    ],
    color: "#2EC4B6",
    photoUrl: "/beaches/playa-maderas.jpg",
    i18n: {
      es: {
        tagline: "La playa de surf icónica de SJDS — olas A-frame consistentes y buen ambiente.",
        difficulty: "Intermedio",
        waveStyle: "Beach break A-frame con izquierdas y derechas",
        bestTide: "Marea baja a media subiendo",
        bestWind: "Viento offshore del este (temprano en la mañana)",
        boardRecommended: "Tabla corta 6'0\"–6'6\" · fun-shape 6'8\"–7'2\"",
        bestSeasons: [
          { months: "Abr – Oct", label: "Swell principal (temporada de lluvias)", quality: 5 },
          { months: "Nov – Mar", label: "Más limpio pero más pequeño (temporada seca)", quality: 3 },
        ],
        hazards: ["Corriente de resaca localizada en olas grandes", "Rocas en el extremo sur con marea baja"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "8 km · 20 min en camioneta / shuttle",
          routeNote: "Camino sin pavimentar — se recomienda 4×4 o shuttle en época de lluvias",
        },
        localTips: [
          "Llega antes de las 8 am — los vientos offshore suaves se acaban a las 10 am",
          "Trae efectivo para estacionamiento (US$3–5) y el bar frente al mar",
          "Botellas de agua vacías — la caminata de regreso por la colina es calurosa",
          "Los peaks se mueven — camina 100 m en cualquier dirección para encontrar tu ola",
        ],
      },
    },
  },

  "playa-remanso": {
    id: "playa-remanso",
    slug: "remanso",
    name: "Playa Remanso",
    tagline: "The friendly one — perfect first-wave beach, walking distance.",
    difficulty: "Beginner",
    difficultyLevel: 1,
    waveStyle: "Gentle beach break, mostly right-handers",
    bestTide: "Mid tide",
    bestWind: "Offshore east / calm",
    boardRecommended: "Longboard 8'0\"+ or foam board — perfect for lessons",
    bestSeasons: [
      { months: "Apr – Nov", label: "Consistent small swells", quality: 4 },
      { months: "Dec – Mar", label: "Small, glassy, ideal for learners", quality: 5 },
    ],
    hazards: ["Shallow section at very low tide", "Occasional strong current mid-beach"],
    directions: {
      from: "San Juan del Sur town",
      distance: "5 km · 15 min walk from La Cruz, or 10 min drive",
      routeNote: "Dirt road with a small entrance fee at the guard hut",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Remanso",
      mapHref: "https://www.google.com/maps/place/Playa+Remanso,+Nicaragua",
    },
    localTips: [
      "Best for surf lessons — most SJDS schools bring students here",
      "The bay is protected → often surfable even on windy days",
      "Small snack bars sell coco fresco and pipa at fair prices",
      "Free parking if you arrive before 8am",
    ],
    color: "#FFB84D",
    photoUrl: "/beaches/playa-remanso.jpg",
    i18n: {
      es: {
        tagline: "La amigable — perfecta para tu primera ola, a distancia caminable.",
        difficulty: "Principiante",
        waveStyle: "Beach break suave, mayormente derechas",
        bestTide: "Marea media",
        bestWind: "Offshore del este / calmo",
        boardRecommended: "Longboard 8'0\"+ o tabla de foam — perfecta para clases",
        bestSeasons: [
          { months: "Abr – Nov", label: "Swells pequeños consistentes", quality: 4 },
          { months: "Dic – Mar", label: "Pequeño, glassy, ideal para aprender", quality: 5 },
        ],
        hazards: ["Sección poco profunda con marea muy baja", "Corriente fuerte ocasional en el centro"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "5 km · 15 min caminando desde La Cruz, o 10 min en carro",
          routeNote: "Camino de tierra con una pequeña cuota de entrada en la caseta",
        },
        localTips: [
          "Ideal para clases de surf — la mayoría de las escuelas de SJDS traen aquí a sus alumnos",
          "La bahía es protegida → surfable incluso en días con viento",
          "Los kioscos venden coco fresco y pipa a buen precio",
          "Estacionamiento gratis si llegas antes de las 8 am",
        ],
      },
    },
  },

  "playa-yankee": {
    id: "playa-yankee",
    slug: "yankee",
    name: "Playa Yankee",
    tagline: "Quiet, consistent, and often uncrowded — a local secret.",
    difficulty: "Intermediate",
    difficultyLevel: 3,
    waveStyle: "Punchy beach break, hollow when swell picks up",
    bestTide: "Low to mid",
    bestWind: "Offshore east / cross-shore acceptable",
    boardRecommended: "Shortboard 6'0\"–6'4\" · step-up 6'6\" on big days",
    bestSeasons: [
      { months: "May – Oct", label: "Prime south swell season", quality: 5 },
      { months: "Nov – Apr", label: "Smaller but cleaner windows", quality: 3 },
    ],
    hazards: ["Isolated — no lifeguards or amenities", "Rocky corners at either end"],
    directions: {
      from: "San Juan del Sur town",
      distance: "18 km · 40 min by truck (rough road)",
      routeNote: "Truck / 4×4 essential — the last 4 km is dirt & river crossings",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Yankee",
      mapHref: "https://www.google.com/maps/place/Playa+Yankee,+Nicaragua",
    },
    localTips: [
      "Bring EVERYTHING — no shops or restaurants at the beach",
      "Cell service is spotty — download offline maps before leaving town",
      "Best paired with Playa Hermosa on the drive back",
      "Empty on weekdays — get there for the dawn session",
    ],
    color: "#FF6F61",
    photoUrl: "/beaches/playa-yankee.jpg",
    i18n: {
      es: {
        tagline: "Tranquila, consistente y usualmente vacía — un secreto local.",
        difficulty: "Intermedio",
        waveStyle: "Beach break potente, tubero cuando el swell sube",
        bestTide: "Baja a media",
        bestWind: "Offshore del este / cross-shore aceptable",
        boardRecommended: "Tabla corta 6'0\"–6'4\" · step-up 6'6\" en días grandes",
        bestSeasons: [
          { months: "May – Oct", label: "Temporada principal de swell del sur", quality: 5 },
          { months: "Nov – Abr", label: "Más pequeño pero con ventanas limpias", quality: 3 },
        ],
        hazards: ["Aislada — sin salvavidas ni servicios", "Rocas en ambos extremos"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "18 km · 40 min en camioneta (camino difícil)",
          routeNote: "Camioneta / 4×4 esencial — los últimos 4 km son de tierra y cruces de río",
        },
        localTips: [
          "Trae TODO — no hay tiendas ni restaurantes en la playa",
          "La señal celular es intermitente — descarga mapas offline antes de salir",
          "Ideal combinar con Playa Hermosa de regreso",
          "Vacía entre semana — llega a la sesión del amanecer",
        ],
      },
    },
  },

  "playa-hermosa": {
    id: "playa-hermosa",
    slug: "hermosa",
    name: "Playa Hermosa",
    tagline: "The all-day beach — shaded loungers, cold drinks, mellow surf.",
    difficulty: "Beginner–Intermediate",
    difficultyLevel: 2,
    waveStyle: "Sandy beach break, forgiving inside — sizes up on south swells",
    bestTide: "Mid to high",
    bestWind: "Offshore east — dawn is glass",
    boardRecommended: "Fun-shape 7'0\"–7'6\" or longboard for beginners; shortboard on head-high days",
    bestSeasons: [
      { months: "May – Oct", label: "Bigger, more powerful swell", quality: 4 },
      { months: "Nov – Apr", label: "Clean, mellow, easier learning days", quality: 4 },
    ],
    hazards: ["Occasional strong current when overhead", "US$3 per-person entrance fee"],
    directions: {
      from: "San Juan del Sur town",
      distance: "16 km · 25 min south by paved road + dirt turn-off",
      routeNote: "Paved most of the way — private beach club at the end has a manned gate",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Hermosa,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Hermosa,+Nicaragua",
    },
    localTips: [
      "Best for a full-day trip — hammocks, sun-loungers, restaurant on-site",
      "The Beach Club rents boards & offers lessons — good for total beginners",
      "Wide, flat sand at low tide → perfect for a barefoot run at sunset",
      "Come here when Maderas is packed — Hermosa spreads people out",
    ],
    color: "#F4A261",
    photoUrl: "/beaches/playa-hermosa.jpg",
    i18n: {
      es: {
        tagline: "La playa de todo el día — reposeras con sombra, bebidas frías y surf tranquilo.",
        difficulty: "Principiante–Intermedio",
        waveStyle: "Beach break arenoso, indulgente por dentro — crece con swells del sur",
        bestTide: "Media a alta",
        bestWind: "Offshore del este — el amanecer es glassy",
        boardRecommended: "Fun-shape 7'0\"–7'6\" o longboard para principiantes; tabla corta en días grandes",
        bestSeasons: [
          { months: "May – Oct", label: "Swell más grande y potente", quality: 4 },
          { months: "Nov – Abr", label: "Limpio, tranquilo, ideal para aprender", quality: 4 },
        ],
        hazards: ["Corriente fuerte ocasional en días grandes", "Cuota de entrada US$3 por persona"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "16 km · 25 min al sur por carretera + desvío de tierra",
          routeNote: "Pavimentado casi todo el trayecto — el club de playa privado al final tiene portón vigilado",
        },
        localTips: [
          "Ideal para pasar el día completo — hamacas, reposeras y restaurante",
          "El Beach Club renta tablas y ofrece clases — bueno para principiantes",
          "Arena amplia y plana con marea baja — perfecto para correr descalzo al atardecer",
          "Ven cuando Maderas está lleno — Hermosa dispersa a la gente",
        ],
      },
    },
  },

  "playa-marsella": {
    id: "playa-marsella",
    slug: "marsella",
    name: "Playa Marsella",
    tagline: "Just north of Maderas — the quiet, sheltered bay for calm swims & peaceful days.",
    difficulty: "Beginner (swimming)",
    difficultyLevel: 1,
    waveStyle: "Sheltered bay — small lapping shore-break, rarely surfable",
    bestTide: "Any — the water is calm at every tide",
    bestWind: "Any wind direction (bay is naturally protected)",
    boardRecommended: "No surfboard needed — bring a paddleboard, kayak, or snorkel mask",
    bestSeasons: [
      { months: "Dec – May", label: "Dry, glass water, warmest swimming days", quality: 5 },
      { months: "Jun – Nov", label: "Green season — occasional afternoon showers", quality: 4 },
    ],
    hazards: [
      "Very few — small children can play in the shallows safely",
      "No lifeguards; watch children in the shore-break during south swells",
      "Limited shade — bring sun protection and water",
    ],
    directions: {
      from: "San Juan del Sur town",
      distance: "9 km · 20 min via the road to Maderas",
      routeNote: "Take the Maderas road (La Chocolata) and stay LEFT at the fork — Marsella is the calmer cousin",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Marsella,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Marsella,+Nicaragua",
    },
    localTips: [
      "This is the sheltered, gentle-waters beach — ideal for families, kids, and non-surfers",
      "Weekday afternoons you might have the sand entirely to yourself — a rare thing on this coast",
      "Great launch spot for stand-up paddleboarding — the bay stays glassy most mornings",
      "Small beachfront restaurants serve fresh fish tacos and cold Toña at fair prices",
      "The northern headland is a lovely 20-min walk with tide-pools at low tide",
      "Pair it with a morning surf at Maderas — 2 min drive away — for the best of both worlds",
      "Sunset is spectacular here — fewer crowds than SJDS Bay, same golden light",
    ],
    color: "#8ECDDD",
    photoUrl: "/beaches/playa-marsella.jpg",
    i18n: {
      es: {
        tagline: "Justo al norte de Maderas — la bahía tranquila y resguardada para nadar en calma.",
        difficulty: "Principiante (natación)",
        waveStyle: "Bahía resguardada — pequeña rompiente en la orilla, rara vez surfeable",
        bestTide: "Cualquiera — el agua está calmada en toda marea",
        bestWind: "Cualquier dirección de viento (la bahía es naturalmente protegida)",
        boardRecommended: "No necesitas tabla de surf — trae paddleboard, kayak o careta de snorkel",
        bestSeasons: [
          { months: "Dic – May", label: "Seca, agua glassy, días más cálidos para nadar", quality: 5 },
          { months: "Jun – Nov", label: "Temporada verde — aguaceros vespertinos ocasionales", quality: 4 },
        ],
        hazards: [
          "Muy pocos — los niños pequeños pueden jugar en la orilla con seguridad",
          "Sin salvavidas; supervisa a los niños en la rompiente durante swells del sur",
          "Poca sombra — trae protección solar y agua",
        ],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "9 km · 20 min por el camino a Maderas",
          routeNote: "Toma el camino a Maderas (La Chocolata) y mantén la IZQUIERDA en la bifurcación — Marsella es la prima tranquila",
        },
        localTips: [
          "Esta es la playa resguardada de aguas suaves — ideal para familias, niños y no-surfistas",
          "En las tardes entre semana puedes tener la arena para ti solo — algo raro en esta costa",
          "Excelente punto de partida para stand-up paddle — la bahía queda glassy la mayoría de las mañanas",
          "Pequeños restaurantes frente al mar sirven tacos de pescado fresco y Toña bien fría a buen precio",
          "El promontorio del norte es una linda caminata de 20 min con pozas de marea con marea baja",
          "Combínala con una surfeada matutina en Maderas — a 2 min en carro — para lo mejor de ambos mundos",
          "El atardecer es espectacular aquí — menos gente que en la bahía de SJDS, con la misma luz dorada",
        ],
      },
    },
  },

  "playa-nacascolo": {
    id: "playa-nacascolo",
    slug: "nacascolo",
    name: "Playa Nacascolo",
    tagline: "A calm, shallow-water bay just 4 km north of San Juan del Sur — swim, kayak, family day.",
    difficulty: "Beginner (swimming)",
    difficultyLevel: 1,
    waveStyle: "Sheltered shallow bay — no surfable waves, calm gentle water",
    bestTide: "Any — the bay is calm at every tide",
    bestWind: "Any direction (the bay is naturally protected)",
    boardRecommended: "Kayak, paddleboard or snorkel mask — no surfboard needed",
    bestSeasons: [
      { months: "Dec – May", label: "Dry season — glassy, warm, easy access", quality: 5 },
      { months: "Jun – Nov", label: "Green season — occasional afternoon showers", quality: 4 },
    ],
    hazards: [
      "Access road is a rough dirt track — best in a 4×4 or on a motorbike",
      "No lifeguards; supervise children in the shore-break",
      "Almost no shade on the sand — bring sun protection",
    ],
    directions: {
      from: "San Juan del Sur town",
      distance: "4 km · about 15 min by vehicle",
      routeNote: "Head north through the Playas de Marsella community — the beach is easily reached from there",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Nacascolo,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Nacascolo,+Nicaragua",
    },
    localTips: [
      "The shallow, calm water makes it a favorite for families with small kids",
      "One of the best spots in the area for kayaking and stand-up paddleboarding",
      "Great for a lazy swim-and-picnic morning — bring your own snacks and water",
      "Access is via the Playas de Marsella community — pass through and follow signs",
      "The sand is fine and golden — great for beach walks at low tide",
      "Pair with a lunch stop at Playa Marsella just a few minutes further along",
    ],
    color: "#7EC8E3",
    photoUrl: "/beaches/playa-nacascolo.jpg",
    i18n: {
      es: {
        tagline: "Una hermosa bahía de aguas tranquilas y poco profundas, a solo 4 km al norte de San Juan del Sur — ideal para nadar, hacer kayak y visitar en familia.",
        difficulty: "Principiante (natación)",
        waveStyle: "Bahía resguardada de aguas poco profundas — sin olas surfeables, agua tranquila y suave",
        bestTide: "Cualquiera — la bahía está calmada en toda marea",
        bestWind: "Cualquier dirección (la bahía es naturalmente protegida)",
        boardRecommended: "Kayak, paddleboard o careta de snorkel — no necesitas tabla de surf",
        bestSeasons: [
          { months: "Dic – May", label: "Temporada seca — glassy, cálido, fácil acceso", quality: 5 },
          { months: "Jun – Nov", label: "Temporada verde — aguaceros vespertinos ocasionales", quality: 4 },
        ],
        hazards: [
          "El camino de acceso es de tierra irregular — mejor en 4×4 o moto",
          "Sin salvavidas; supervisa a los niños en la orilla",
          "Casi no hay sombra en la arena — trae protección solar",
        ],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "4 km · unos 15 minutos en vehículo",
          routeNote: "Ve hacia el norte atravesando la comunidad de Playas de Marsella — se accede fácilmente desde allí",
        },
        localTips: [
          "El agua poco profunda y tranquila la hace favorita para familias con niños pequeños",
          "Uno de los mejores lugares de la zona para hacer kayak y stand-up paddle",
          "Perfecta para una mañana de nadar y hacer picnic — trae tus propios snacks y agua",
          "Se accede a través de la comunidad Playas de Marsella — atraviésala y sigue las señales",
          "La arena es fina y dorada — ideal para caminatas con marea baja",
          "Combínala con un almuerzo en Playa Marsella a pocos minutos por el mismo camino",
        ],
      },
    },
  },

  "playa-majagual": {
    id: "playa-majagual",
    slug: "majagual",
    name: "Playa Majagual",
    tagline: "Golden, empty, undeveloped — the calm neighbor just north of Maderas.",
    difficulty: "All levels (swimming)",
    difficultyLevel: 1,
    waveStyle: "Small beach break — occasional surfable waves on big south swells",
    bestTide: "Mid tide",
    bestWind: "Calm mornings",
    boardRecommended: "Longboard or paddleboard — the water is usually mellow",
    bestSeasons: [
      { months: "Dec – Apr", label: "Dry season, calm swimming", quality: 5 },
      { months: "May – Nov", label: "Green-season swell keeps it interesting", quality: 3 },
    ],
    hazards: [
      "Very little shade — bring a shelter or wide-brim hat",
      "No lifeguards; no shops on the sand",
      "Undeveloped access — sandy road, best in a 4×4",
    ],
    directions: {
      from: "San Juan del Sur town",
      distance: "10 km · 25 min by truck (Maderas road + right fork)",
      routeNote: "From the Maderas road, take the right-hand fork toward the Morgan's Rock direction",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Majagual,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Majagual,+Nicaragua",
    },
    localTips: [
      "Long stretch of golden sand, almost no built development",
      "Perfect for a long barefoot walk at low tide",
      "Best paired with a surf day at Maderas next door",
      "Bring EVERYTHING (water, snacks, umbrella) — nothing for sale on the beach",
    ],
    color: "#F1B24A",
    photoUrl: "/beaches/playa-majagual.jpg",
    i18n: {
      es: {
        tagline: "Dorada, vacía y sin desarrollar — la vecina tranquila justo al norte de Maderas.",
        difficulty: "Todos los niveles (natación)",
        waveStyle: "Beach break pequeño — olas surfeables ocasionales en swells grandes del sur",
        bestTide: "Media",
        bestWind: "Mañanas calmas",
        boardRecommended: "Longboard o paddleboard — el agua suele estar tranquila",
        bestSeasons: [
          { months: "Dic – Abr", label: "Temporada seca, nado tranquilo", quality: 5 },
          { months: "May – Nov", label: "El swell de temporada verde la mantiene interesante", quality: 3 },
        ],
        hazards: [
          "Muy poca sombra — trae una carpa o sombrero de ala ancha",
          "Sin salvavidas ni tiendas en la arena",
          "Acceso poco desarrollado — camino arenoso, mejor en 4×4",
        ],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "10 km · 25 min en camioneta (camino a Maderas + desvío a la derecha)",
          routeNote: "Desde el camino a Maderas, toma el desvío a la derecha hacia Morgan's Rock",
        },
        localTips: [
          "Largo tramo de arena dorada, casi sin desarrollo",
          "Perfecta para una larga caminata descalzo con marea baja",
          "Ideal combinar con un día de surf en Maderas al lado",
          "Trae TODO (agua, snacks, sombrilla) — no hay nada a la venta en la playa",
        ],
      },
    },
  },

  "playa-tamarindo": {
    id: "playa-tamarindo",
    slug: "tamarindo",
    name: "Playa Tamarindo",
    tagline: "Small, uncrowded cove just south of Remanso — hidden and surf-able on the right tide.",
    difficulty: "Intermediate",
    difficultyLevel: 3,
    waveStyle: "Compact beach break — works best on a mid-tide push",
    bestTide: "Mid tide (rising)",
    bestWind: "Offshore east",
    boardRecommended: "Shortboard 6'0\"–6'4\" or fun-shape",
    bestSeasons: [
      { months: "May – Oct", label: "South swell fires it up", quality: 4 },
      { months: "Nov – Apr", label: "Cleaner but smaller — often flat", quality: 2 },
    ],
    hazards: ["Rocky corners at low tide", "Access through a private development"],
    directions: {
      from: "San Juan del Sur town",
      distance: "6 km · 15 min south — access through the Remanso beach-club road",
      routeNote: "Drive to Playa Remanso and take a LEFT just before you reach the sand",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+Tamarindo,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+Tamarindo,+Nicaragua",
    },
    localTips: [
      "Usually completely empty — a hidden gem next to the more popular Remanso",
      "Only surfable when the tide is right — check a tide chart before you drive out",
      "Pair with a Remanso session for a two-beach morning",
      "Small footprint means no crowds — but also no shops or shade",
    ],
    color: "#B2846B",
    photoUrl: "/beaches/playa-tamarindo.jpg",
    i18n: {
      es: {
        tagline: "Cala pequeña y sin gente justo al sur de Remanso — escondida y surfeable con la marea correcta.",
        difficulty: "Intermedio",
        waveStyle: "Beach break compacto — funciona mejor con marea media subiendo",
        bestTide: "Media (subiendo)",
        bestWind: "Offshore del este",
        boardRecommended: "Tabla corta 6'0\"–6'4\" o fun-shape",
        bestSeasons: [
          { months: "May – Oct", label: "El swell del sur la enciende", quality: 4 },
          { months: "Nov – Abr", label: "Más limpia pero más pequeña — a menudo plana", quality: 2 },
        ],
        hazards: ["Rocas en las esquinas con marea baja", "Acceso por un desarrollo privado"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "6 km · 15 min al sur — acceso por el camino del club de playa Remanso",
          routeNote: "Maneja hasta Playa Remanso y toma la IZQUIERDA justo antes de llegar a la arena",
        },
        localTips: [
          "Usualmente completamente vacía — una joya escondida junto a la más popular Remanso",
          "Solo surfeable con la marea correcta — revisa la tabla de mareas antes de ir",
          "Ideal combinar con una sesión en Remanso para una mañana de dos playas",
          "Espacio pequeño significa sin multitudes — pero tampoco tiendas ni sombra",
        ],
      },
    },
  },

  "playa-el-coco": {
    id: "playa-el-coco",
    slug: "el-coco",
    name: "Playa El Coco",
    tagline: "The escape south — long empty sand, cliff-lined bay, weekday hush.",
    difficulty: "All levels (swimming)",
    difficultyLevel: 2,
    waveStyle: "Wide sandy beach — soft shore-break, gentle right-handers on south swells",
    bestTide: "Mid tide for swimming; mid-high for the peelers on the north end",
    bestWind: "Offshore east / calm mornings",
    boardRecommended: "Longboard or fun-shape — the waves are mellow when they do show",
    bestSeasons: [
      { months: "Dec – Apr", label: "Dry season — flat, warm, empty", quality: 5 },
      { months: "May – Nov", label: "Occasional south swell + green-season sunsets", quality: 4 },
    ],
    hazards: [
      "No lifeguards or medical services — closest is 30 min back to SJDS",
      "Weak cell signal on the last few kilometers",
      "Watch for the rip that forms at the south end during larger swells",
    ],
    directions: {
      from: "San Juan del Sur town",
      distance: "23 km · 45 min by truck south — pavement most of the way, then dirt",
      routeNote: "Head south past Escameca — the road ends at the beach and parking lot. Perfect motorcycle / ATV ride from town.",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Playa+El+Coco,+Nicaragua",
      mapHref: "https://www.google.com/maps/place/Playa+El+Coco,+Nicaragua",
    },
    localTips: [
      "Come on a weekday to have this whole 2-km stretch essentially to yourself",
      "The winding road out is half the fun — grab a rental motorbike or ATV in town",
      "Small tiendas at the south end sell cold drinks, snacks & the odd fresh-caught fish",
      "Pair with a stop at Playa La Flor on the way back — 8 min further south",
      "Bring cash — no ATMs, no card readers, no Wi-Fi",
      "Sunset here feels far away from everything — worth the drive",
      "The cliff at the south end has a short scramble to a natural tide-pool spot",
    ],
    color: "#E76F51",
    photoUrl: "/beaches/playa-el-coco.jpg",
    i18n: {
      es: {
        tagline: "El escape al sur — arena larga y vacía, bahía flanqueada por acantilados, silencio entre semana.",
        difficulty: "Todos los niveles (natación)",
        waveStyle: "Playa ancha y arenosa — rompiente suave, derechas leves en swells del sur",
        bestTide: "Media para nadar; media-alta para las olas del extremo norte",
        bestWind: "Offshore del este / mañanas calmas",
        boardRecommended: "Longboard o fun-shape — las olas son tranquilas cuando aparecen",
        bestSeasons: [
          { months: "Dic – Abr", label: "Temporada seca — plano, cálido, vacío", quality: 5 },
          { months: "May – Nov", label: "Swell del sur ocasional + atardeceres de temporada verde", quality: 4 },
        ],
        hazards: [
          "Sin salvavidas ni servicios médicos — el más cercano está a 30 min en SJDS",
          "Señal celular débil en los últimos kilómetros",
          "Cuidado con la resaca que se forma en el extremo sur con olas grandes",
        ],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "23 km · 45 min en camioneta al sur — pavimento casi todo el trayecto, luego tierra",
          routeNote: "Ve al sur pasando Escameca — el camino termina en la playa y el estacionamiento. Ideal en moto o ATV desde el pueblo.",
        },
        localTips: [
          "Ven entre semana para tener los 2 km de arena prácticamente para ti solo",
          "El camino serpenteante es parte de la aventura — renta una moto o ATV en el pueblo",
          "Pequeñas tiendas al sur venden bebidas frías, snacks y pescado fresco de vez en cuando",
          "Combínala con una parada en Playa La Flor de regreso — 8 min más al sur",
          "Trae efectivo — no hay cajeros, ni datáfonos, ni Wi-Fi",
          "El atardecer aquí se siente lejos de todo — vale la pena el viaje",
          "El acantilado del sur tiene una trepada corta hacia una poza natural de marea",
        ],
      },
    },
  },

  "playa-la-flor": {
    id: "playa-la-flor",
    slug: "la-flor",
    name: "Playa La Flor",
    tagline: "Wildlife refuge & sea-turtle nesting sanctuary — surf light, respect the turtles.",
    difficulty: "Beginner–Intermediate",
    difficultyLevel: 2,
    waveStyle: "Soft beach break in a protected refuge",
    bestTide: "Mid tide",
    bestWind: "Calm mornings",
    boardRecommended: "Longboard 8'0\"+ or fun-shape 7'2\" — the wave is mellow",
    bestSeasons: [
      { months: "Jul – Jan", label: "Peak Olive-Ridley arribada season", quality: 5 },
      { months: "Feb – Jun", label: "Fewer turtles, cleaner surf sessions", quality: 3 },
    ],
    hazards: ["Protected refuge — surfing is regulated during arribadas", "Ranger fee applies (US$5)"],
    directions: {
      from: "San Juan del Sur town",
      distance: "20 km · 40 min by truck",
      routeNote: "Refuge entrance has a ranger booth — pay the fee, respect the rules",
      driveHref: "https://www.google.com/maps/dir/San+Juan+del+Sur/Refugio+La+Flor",
      mapHref: "https://www.google.com/maps/place/Refugio+de+Vida+Silvestre+La+Flor",
    },
    localTips: [
      "Combine with a sunset arribada tour — thousands of turtles arriving at once",
      "Don't touch, disturb, or shine lights at hatching turtles",
      "Rangers close the beach when critical arribadas are happening — check ahead",
      "The refuge is one of the world's most important Olive-Ridley sites — a privilege to surf here",
    ],
    color: "#4CAF50",
    photoUrl: "/beaches/playa-la-flor.jpg",
    i18n: {
      es: {
        tagline: "Refugio de vida silvestre y santuario de tortugas marinas — surfea suave, respeta a las tortugas.",
        difficulty: "Principiante–Intermedio",
        waveStyle: "Beach break suave dentro de un refugio protegido",
        bestTide: "Media",
        bestWind: "Mañanas calmas",
        boardRecommended: "Longboard 8'0\"+ o fun-shape 7'2\" — la ola es tranquila",
        bestSeasons: [
          { months: "Jul – Ene", label: "Temporada pico de arribada Olive-Ridley", quality: 5 },
          { months: "Feb – Jun", label: "Menos tortugas, sesiones de surf más limpias", quality: 3 },
        ],
        hazards: ["Refugio protegido — el surf se regula durante arribadas", "Cuota de guardaparques (US$5)"],
        directions: {
          from: "Casco urbano de San Juan del Sur",
          distance: "20 km · 40 min en camioneta",
          routeNote: "La entrada al refugio tiene caseta de guardaparques — paga la cuota y respeta las reglas",
        },
        localTips: [
          "Combínala con un tour de arribada al atardecer — miles de tortugas llegando a la vez",
          "No toques, molestes ni ilumines a las tortugas al eclosionar",
          "Los guardaparques cierran la playa durante arribadas críticas — consulta antes de ir",
          "El refugio es uno de los sitios Olive-Ridley más importantes del mundo — un privilegio surfear aquí",
        ],
      },
    },
  },
};

export const BEACH_SLUGS = Object.values(BEACH_GUIDES).map((b) => b.slug);

/**
 * Return a beach guide with its translatable fields replaced by the caller's
 * language. Falls back to English (the source) when a key is missing so newly
 * added beaches never render blank.
 */
export function localizeBeach(beach, lang) {
  if (!beach) return beach;
  const tr = beach.i18n?.[lang];
  if (!tr) return beach;
  return {
    ...beach,
    tagline: tr.tagline || beach.tagline,
    difficulty: tr.difficulty || beach.difficulty,
    waveStyle: tr.waveStyle || beach.waveStyle,
    bestTide: tr.bestTide || beach.bestTide,
    bestWind: tr.bestWind || beach.bestWind,
    boardRecommended: tr.boardRecommended || beach.boardRecommended,
    bestSeasons: Array.isArray(tr.bestSeasons) && tr.bestSeasons.length
      ? tr.bestSeasons.map((s, i) => ({ ...beach.bestSeasons[i], ...s }))
      : beach.bestSeasons,
    hazards: Array.isArray(tr.hazards) && tr.hazards.length ? tr.hazards : beach.hazards,
    localTips: Array.isArray(tr.localTips) && tr.localTips.length ? tr.localTips : beach.localTips,
    directions: {
      ...beach.directions,
      ...(tr.directions || {}),
    },
  };
}
